import React, { useEffect, useState } from "react";
import moment from "moment";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";
import {
  LuAlertCircle,
  LuCalendar,
  LuCreditCard,
  LuIndianRupee,
  LuListFilter,
  LuMapPin,
  LuPhone,
  LuUser,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditEmployee,
  getIsEmployeeModalOpen,
  toggleEmployeeModal,
} from "../../features/employees/employeesSlice";
import { iconSelectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/employees";
import { ShieldIcon } from "../icons";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";

type Props = {
  handleSaveEmployee: (data: IFormInput) => void;
};

const EmployeeFormModal = ({ handleSaveEmployee }: Props) => {
  const dispatch = useAppDispatch();
  const editEmployee = useAppSelector(getEditEmployee);
  const isEmployeeModalOpen = useAppSelector(getIsEmployeeModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFixedOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "ફિક્સ નથી",
    },
    {
      value: 2,
      label: "ફિક્સ છે",
    },
  ]);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "સક્રિય",
    },
    {
      value: 0,
      label: "નિષ્ક્રિય",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      employee_code: "",
      name: "",
      phone: "",
      address: "",
      amount: null,
      is_fixed: 1,
      status: 1,
      created_at: moment().format("YYYY-MM-DD"),
    },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveEmployee(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleEmployeeModal(false));
  };

  useEffect(() => {
    if (editEmployee && editEmployee.id) {
      reset({
        employee_code: editEmployee.employee_code || "",
        name: editEmployee.name,
        phone: editEmployee.phone || "",
        address: editEmployee.address || "",
        amount: editEmployee.amount,
        is_fixed: editEmployee.is_fixed,
        status: editEmployee.status,
        created_at: moment(editEmployee.created_at).format("YYYY-MM-DD"),
      });
    } else {
      reset({
        employee_code: "",
        name: "",
        phone: "",
        address: "",
        amount: null,
        is_fixed: 1,
        status: 1,
        created_at: moment().format("YYYY-MM-DD"),
      });
    }
  }, [editEmployee, isEmployeeModalOpen, reset]);

  return (
    <Dialog open={isEmployeeModalOpen === true} onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>કામદાર ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                ફિક્સ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.is_fixed && "error")}>
                <ShieldIcon className={formSelectIconClass} />
                <Controller
                  {...register("is_fixed", { required: true })}
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      value={
                        isFixedOption.find(
                          (val) => val.value === field.value
                        ) || null
                      }
                      options={isFixedOption}
                      placeholder="ફિક્સ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.is_fixed && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) => setValue("is_fixed", val?.value)}
                    />
                  )}
                />
              </div>
              {errors.is_fixed && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  ફિક્સ સિલેક્ટ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                કામદાર નું નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <LuUser className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="કામદાર નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", { required: true })}
                />
                {errors.name && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કામદાર નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>કાર્ડ નંબર</label>
              <div className="relative">
                <LuCreditCard className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="કાર્ડ નંબર દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("employee_code", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>મોબાઇલ નંબર</label>
              <div className="relative">
                <LuPhone className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="મોબાઇલ નંબર દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("phone", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                1 કલાક નો પગાર <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.amount && "error")}>
                <LuIndianRupee className={formFieldIconClass} />
                <Input
                  type="number"
                  step="any"
                  placeholder="કામદારનો 1 કલાક નો પગાર દાખલ કરો"
                  className={fieldInputClass(!!errors.amount)}
                  {...register("amount", { required: true, min: 0 })}
                />
                {errors.amount && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.amount && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કામદારનો 1 કલાક નો પગાર દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>એડ્રેસ</label>
              <div className="relative">
                <LuMapPin className={formFieldIconClass} />
                <Textarea
                  rows={3}
                  placeholder="કામદારનુ એડ્રેસ દાખલ કરો"
                  className={fieldTextareaClass()}
                  {...register("address", { required: false })}
                />
              </div>
            </div>

            {!editEmployee && (
              <div className="space-y-2">
                <label className={fieldLabelClass}>
                  તારીખ <span className="text-red-500">*</span>
                </label>
                <div className={cx("relative", errors.created_at && "error")}>
                  <LuCalendar className={formFieldIconClass} />
                  <Input
                    type="date"
                    className={fieldInputClass(!!errors.created_at)}
                    {...register("created_at", { required: true })}
                  />
                  {errors.created_at && (
                    <LuAlertCircle className={fieldErrorIconClass} />
                  )}
                </div>
                {errors.created_at && (
                  <p className={fieldErrorTextClass}>
                    <LuAlertCircle className="h-4 w-4 mr-1" />
                    તારીખ સિલેક્ટ કરો
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                સ્ટેટ્સ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.status && "error")}>
                <LuListFilter className={formSelectIconClass} />
                <Controller
                  {...register("status", { required: true })}
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      value={
                        statusOption.find((val) => val.value === field.value) ||
                        null
                      }
                      options={statusOption}
                      placeholder="સ્ટેટ્સ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.status && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) => setValue("status", val?.value)}
                    />
                  )}
                />
              </div>
              {errors.status && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  સ્ટેટ્સ સિલેક્ટ કરો
                </p>
              )}
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeFormModal;
